/**
 * Sky High — lightweight site motion
 *
 * Strategy (perf-first):
 *  - One IntersectionObserver for all reveals
 *  - Only transform/opacity (CSS)
 *  - Cap targets; unobserve after first in-view
 *  - No scroll listeners, no rAF loops, no MutationObserver
 *  - One delayed re-scan for late app widgets
 *  - Bail on prefers-reduced-motion / save-data / tiny screens optional
 */
(function () {
  'use strict';

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  // Optional: respect Save-Data
  try {
    if (navigator.connection && navigator.connection.saveData) return;
  } catch (e) {}

  var MAX_TARGETS = 36;
  var STAGGER_MAX_KIDS = 8;
  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var io = null;
  var scheduled = false;

  function markSeen(el) {
    if (observed) observed.add(el);
  }

  function wasSeen(el) {
    return observed ? observed.has(el) : el.getAttribute('data-rm') === '1';
  }

  function ensureIO() {
    if (io) return io;
    if (!('IntersectionObserver' in window)) {
      io = {
        observe: function (el) {
          el.classList.add('is-in');
        },
        unobserve: function () {},
      };
      return io;
    }
    io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      },
      {
        root: null,
        // Start a bit early so motion finishes as user arrives
        rootMargin: '0px 0px -6% 0px',
        threshold: 0.08,
      }
    );
    return io;
  }

  function isNearViewport(el) {
    try {
      var rect = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      // Already on screen (or nearly) — never flash-hide
      return rect.top < vh * 0.95 && rect.bottom > -40;
    } catch (e) {
      return true;
    }
  }

  function addReveal(el, stagger) {
    if (!el || wasSeen(el)) return false;
    if (el.closest && el.closest('.hero-cinematic')) return false;

    markSeen(el);
    el.setAttribute('data-rm', '1');

    // Above-the-fold: mark in immediately (no opacity:0 flash)
    if (isNearViewport(el)) {
      if (stagger) {
        el.classList.add('rm-stagger', 'home-reveal-stagger', 'is-in');
      } else {
        el.classList.add('rm-reveal', 'home-reveal', 'is-in');
      }
      return true;
    }

    if (stagger) {
      el.classList.add('rm-stagger', 'home-reveal-stagger');
    } else {
      el.classList.add('rm-reveal', 'home-reveal');
    }
    ensureIO().observe(el);
    return true;
  }

  function pickFromSection(sec, isFirstMain) {
    var added = 0;

    // Skip pure hero (has own CSS cascade)
    if (sec.querySelector && sec.querySelector('.hero-cinematic')) {
      return 0;
    }

    // Headers / copy blocks
    var headerSelectors = [
      '.title-wrapper-with-link',
      '.popular-categories__header',
      '.pickup-teaser__copy',
      '.image-with-text__content',
      '.rich-text__blocks',
      '.collapsible-content__header',
      '.newsletter__wrapper',
      '.collection-hero__text-wrapper',
      '.product__info-container > *:first-child',
      '.main-page-title',
      '.article-template__hero-container',
      'h1',
      'h2',
    ];

    var h;
    for (var i = 0; i < headerSelectors.length; i++) {
      h = sec.querySelector(headerSelectors[i]);
      if (h && addReveal(h, false)) {
        added++;
        break;
      }
    }

    // Grids / carousels — stagger only if small
    var gridSelectors = [
      '.popular-categories__grid',
      '.product-grid',
      '.collection .product-grid',
      '.multicolumn-list',
      '.contains-card',
      '.blog-articles',
      '.jdgm-carousel',
      '.pickup-teaser__inner',
    ];

    for (var g = 0; g < gridSelectors.length; g++) {
      var grid = sec.querySelector(gridSelectors[g]);
      if (!grid) continue;
      var kids = grid.children ? grid.children.length : 0;
      if (kids > 0 && kids <= STAGGER_MAX_KIDS) {
        if (addReveal(grid, true)) added++;
      } else if (addReveal(grid, false)) {
        added++;
      }
      break;
    }

    // Product cards on collection (container only if large — already handled)
    return added;
  }

  function scan() {
    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;

    var count = 0;
    var sections = main.querySelectorAll(':scope > .shopify-section');
    if (!sections.length) {
      sections = main.querySelectorAll('.shopify-section');
    }

    for (var s = 0; s < sections.length; s++) {
      if (count >= MAX_TARGETS) break;
      count += pickFromSection(sections[s], s === 0);
    }

    // Footer once
    if (count < MAX_TARGETS) {
      var foot = document.querySelector('.footer, .footer__content-top, footer');
      if (foot) addReveal(foot, false);
    }
  }

  function run() {
    if (scheduled) return;
    scheduled = true;
    // Yield to first paint
    var kick = function () {
      scheduled = false;
      try {
        scan();
      } catch (err) {}
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        requestAnimationFrame(kick);
      });
    } else {
      setTimeout(kick, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Late widgets (Rebuy / Judge.me) — single cheap re-scan, not a live observer
  setTimeout(run, 1100);

  // Collection infinite-load / filter re-render (event if theme fires it)
  document.addEventListener(
    'shopify:section:load',
    function () {
      run();
    },
    { passive: true }
  );
})();
